import { Injectable } from '@angular/core'
import { CandidateHomeNavigationService } from '@candidate/app/services/candidate-home-navigation.service'
import { CandidateResumeService } from '@candidate/app/services/resume/candidate-resume.service'
import { ResumeTokenService, copyShareUrl } from '@candidate/app/services/resume/resume-token.service'
import { combineWithInput } from '@engineering11/stream-utility'
import { isNotNil } from '@engineering11/utility'
import { E11Logger } from '@engineering11/web-api-error'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import { from } from 'rxjs'
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators'
import { selectors } from '../selectors'
import {
  CreateNewResume,
  CreateNewResumeSuccess,
  DeactivateResumeToken,
  DeactivateResumeTokenSuccess,
  DeleteResume,
  DeleteResumeSuccess,
  DeleteResumeToken,
  DeleteResumeTokenSuccess,
  ErrorAction,
  GetAllResumeTokens,
  GetAllResumeTokensSuccess,
  GetAllResumes,
  GetAllResumesSuccess,
  GetNewResumeThreshold,
  GetNewResumeThresholdSuccess,
  NavigateToResume,
  OnInitResume,
  ReactivateResumeToken,
  ReactivateResumeTokenSuccess,
  ResumeActionTypes,
  ShareResume,
  ShareResumeSuccess,
  UPDATE_RESUME_ACTION_TYPES,
  UpdateResumeAction,
  UpdateResumeSuccess,
} from './resume.actions'

@Injectable()
export class ResumeEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private resumeService: CandidateResumeService,
    private resumeTokenService: ResumeTokenService,
    private logger: E11Logger,
    private candidateHomeNavigationService: CandidateHomeNavigationService
  ) {}

  ngrxOnInitEffects(): Action {
    return { type: ResumeActionTypes.onInitResume }
  }

  onInit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<OnInitResume>(ResumeActionTypes.onInitResume),
      switchMap(_ => this.store.select(selectors.getCurrentUserId)),
      filter(isNotNil),
      tap(userId => this.store.dispatch(new GetNewResumeThreshold(userId))),
      map(userId => new GetAllResumes(userId))
    )
  })

  onGetNewResumeThreshold$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetNewResumeThreshold>(ResumeActionTypes.getNewResumeThreshold),
      switchMap(({ userId }) => this.resumeService.getNewResumeThreshold(userId)),
      map(threshold => new GetNewResumeThresholdSuccess(threshold))
    )
  )

  onGetAllResumes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllResumes>(ResumeActionTypes.getAllResumes),
      switchMap(({ userId }) => this.resumeService.getAllForUser(userId)),
      map(resumes => new GetAllResumesSuccess(resumes))
    )
  )

  onGetAllResumesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllResumesSuccess>(ResumeActionTypes.getAllResumesSuccess),
      map(action => new GetAllResumeTokens())
    )
  )

  onCreateResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateNewResume>(ResumeActionTypes.createNewResume),
      mergeMap(action => this.resumeService.create(action.payload)),
      map(result => new CreateNewResumeSuccess(result))
    )
  )

  onCreateResumeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateNewResumeSuccess>(ResumeActionTypes.createNewResumeSuccess),
      map(action => new NavigateToResume(action.payload.id)) // Navigate right after we create
    )
  )

  onNavigateToResume$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<NavigateToResume>(ResumeActionTypes.navigateToResume),
        map(action => this.candidateHomeNavigationService.resumeById(action.payload))
      ),
    { dispatch: false }
  )

  onUpdateResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateResumeAction>(...UPDATE_RESUME_ACTION_TYPES),
      mergeMap(action => from(this.resumeService.update(action.payload)).pipe(map(_ => action))),
      map(action => new UpdateResumeSuccess(action.payload))
    )
  )

  fetchNewThreshold$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateNewResumeSuccess | DeleteResumeSuccess>(ResumeActionTypes.createNewResumeSuccess, ResumeActionTypes.deleteResumeSuccess),
      switchMap(_ => this.store.select(selectors.getCurrentUserId).pipe(filter(isNotNil))),
      map(userId => new GetNewResumeThreshold(userId))
    )
  )

  onDeleteResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteResume>(ResumeActionTypes.deleteResume),
      mergeMap(action => from(this.resumeService.delete(action.id)).pipe(map(_ => action))),
      map(action => new DeleteResumeSuccess(action.id))
    )
  )

  onShareResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ShareResume>(ResumeActionTypes.shareResume),
      mergeMap(action => this.resumeTokenService.createAccessToken(action.payload)),
      tap(response => copyShareUrl(response)),
      map(response => new ShareResumeSuccess(response))
    )
  )

  onGetAllResumeTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllResumeTokens>(ResumeActionTypes.getAllResumeTokens),
      switchMap(action => this.resumeTokenService.getAllTokens()),
      map(response => new GetAllResumeTokensSuccess(response))
    )
  )

  onDeactivateResumeToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeactivateResumeToken>(ResumeActionTypes.deactivateResumeToken),
      mergeMap(action => this.resumeTokenService.deactivateAccessToken(action.payload.token).pipe(map(combineWithInput(action)))),
      map(([action, response]) => new DeactivateResumeTokenSuccess(action.payload))
    )
  )

  onReactivateResumeToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReactivateResumeToken>(ResumeActionTypes.reactivateResumeToken),
      mergeMap(action => this.resumeTokenService.reactivateAccessToken(action.payload.token).pipe(map(combineWithInput(action)))),
      map(([action, response]) => new ReactivateResumeTokenSuccess(action.payload))
    )
  )

  onDeleteResumeToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteResumeToken>(ResumeActionTypes.deleteResumeToken),
      mergeMap(action => this.resumeTokenService.deleteAccessToken(action.payload.token).pipe(map(combineWithInput(action)))),
      map(([action, response]) => new DeleteResumeTokenSuccess(action.payload))
    )
  )

  onError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ErrorAction>(ResumeActionTypes.error),
        map(action => this.logger.error(action.payload))
      ),
    { dispatch: false }
  )
}
